/**
 * Thermostat Display Screen
 * - prominently display the three main readings of the thermostat
 * - if offline display a warning label with a timestamp
 */
import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { SetPoints } from '../setpoints/setpoints';
import { DisplayValue } from '../../../../shared-ui';
import { ViewMode } from '../../pendingUpdateState';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';

interface DisplayScreenProps {
  separatePower?: boolean;
  powerMode?: string | null;
  setpoint?: number;
  heatTargetDigits?: number;
  coolTargetDigits?: number;
  supportsSingleSetpointAutoMode?: boolean;
  autoTargetConfirmed?: boolean;
  canEdit?: boolean;
  canEditHeat?: boolean;
  canEditCool?: boolean;
  coolSetpoint?: number;
  heatSetpoint?: number;
  humidity?: number;
  systemType?: string;
  operatingMode?: string;
  operatingState?: string;
  preferredUnits?: string;
  supportsHumidityMonitoring?: boolean;
  viewMode?: ViewMode;
  temperature?: number;
  timestamp?: number;
  handleTargetChange: (
    targetMode: 'heat' | 'cool' | 'setpoint',
    delta: number,
  ) => void;
  handleChange: (changes: Record<string, number | string | boolean>) => void;
}

export function DisplayScreen(props: DisplayScreenProps) {
  const {
    preferredUnits,
    supportsHumidityMonitoring,
    temperature,
    heatSetpoint,
    coolSetpoint,
    humidity,
    systemType,
    operatingMode,
    operatingState,
    viewMode,
    timestamp,
    handleTargetChange,
    handleChange,
  } = props;
  const border = (viewMode === ViewMode.OFFLINE && '4px solid red') || '';
  const isEditing =
    viewMode === ViewMode.EDIT || viewMode === ViewMode.EDIT_UNCHANGED;
  const poweredOff = props.separatePower
    ? props.powerMode === 'Off'
    : operatingMode === 'Off';
  const targetCandidates =
    operatingMode === 'Heat'
      ? [heatSetpoint]
      : operatingMode === 'Cool'
        ? [coolSetpoint]
        : operatingMode === 'Auto' && props.supportsSingleSetpointAutoMode
          ? [props.setpoint]
          : [heatSetpoint, coolSetpoint];
  const rememberedTargets = targetCandidates.filter(
    (value) => typeof value === 'number' && Number.isFinite(value),
  );
  if (
    operatingMode === 'Fan' &&
    rememberedTargets.length === 0 &&
    typeof props.setpoint === 'number' &&
    Number.isFinite(props.setpoint)
  )
    rememberedTargets.push(props.setpoint);
  const rememberedTargetLabel = rememberedTargets.length
    ? `Setpoints: ${rememberedTargets.join(' | ')}`
    : undefined;
  let formatTimestamp = '';

  if (timestamp && timestamp > 0) {
    formatTimestamp = dayjs(timestamp).format('LLLL');
  }

  return (
    <Box sx={{ border, px: 0, py: 0 }}>
      {/* Display current temp, set temperatures, and current humidity */}
      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-evenly'}
        divider={<Divider orientation="vertical" flexItem />}
      >
        {/* Current Temp */}
        <DisplayValue
          value={temperature}
          label="Temperature"
          units={preferredUnits}
          mode={operatingState} // mode={operatingMode} - switched to operatingState for current values to mimic old super behavior
          size="h2"
          unitsSize="22px"
        />
        {/* Setpoints - With buttons in edit mode*/}
        {poweredOff ? (
          <DisplayValue
            value="Off"
            label={
              props.separatePower
                ? isEditing
                  ? undefined
                  : rememberedTargetLabel
                : `Setpoints:
                  ${
                    !systemType
                      ? 'None'
                      : ['HeatAndCool', 'SmartAHU'].includes(systemType)
                        ? heatSetpoint + ' | ' + coolSetpoint
                        : systemType === 'Cool'
                          ? coolSetpoint
                          : heatSetpoint
                  }`
            }
            mode="Off"
            size="h2"
            unitsSize="22px"
          />
        ) : props.separatePower && operatingMode === 'Fan' ? (
          <Stack alignItems="center">
            <Typography variant="h5">FAN ONLY</Typography>
            <Typography variant="caption">No temperature target</Typography>
          </Stack>
        ) : (
          <SetPoints
            heatTargetDigits={props.heatTargetDigits}
            coolTargetDigits={props.coolTargetDigits}
            canEditHeat={props.canEditHeat}
            canEditCool={props.canEditCool}
            setpoint={props.setpoint}
            autoTargetConfirmed={props.autoTargetConfirmed}
            supportsSingleSetpointAutoMode={
              props.supportsSingleSetpointAutoMode
            }
            operatingMode={operatingMode}
            heatSetpoint={heatSetpoint}
            coolSetpoint={coolSetpoint}
            units={preferredUnits}
            editable={
              props.canEdit !== false &&
              (viewMode === ViewMode.EDIT ||
                viewMode === ViewMode.EDIT_UNCHANGED)
            }
            handleTargetChange={handleTargetChange}
            handleChange={handleChange}
          />
        )}
        {/* Current RH */}
        {supportsHumidityMonitoring === true ? (
          <DisplayValue
            value={humidity}
            units={'%'}
            label="relative humidity"
            mode="Humidity" //hard-coded, since the operating mode and state never change or even exist.
            size="h2"
            unitsSize="22px"
          />
        ) : null}
      </Stack>
      {viewMode === ViewMode.OFFLINE ? (
        <Box
          sx={{
            margin: '0px',
            marginTop: '4px',
            marginY: '4px',
            padding: '0px',
            width: '100%',
          }}
          height="auto"
          textAlign={'center'}
        >
          <Button
            disabled
            variant={'contained'}
            sx={{
              width: '95%',
              fontSize: '14px',
              lineHeight: '1',
              '&.Mui-disabled': {
                backgroundColor: 'red',
                color: 'white',
                lineHeight: '1',
              },
            }}
          >
            <Stack>
              <Typography variant={'h4'}>Device Offline</Typography>
              <span>{formatTimestamp}</span>
            </Stack>
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
